import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Requisitions from "./pages/Requisitions";
import RequisitionDetail from "./pages/RequisitionDetail";
import Candidates from "./pages/Candidates";
import CandidateDetail from "./pages/CandidateDetail";
import AgentChat from "./pages/AgentChat";
import Interviews from "./pages/Interviews";
import Offers from "./pages/Offers";
import MasterData from "./pages/MasterData";
import Integrations from "./pages/Integrations";
import AuditLog from "./pages/AuditLog";
import Careers from "./pages/Careers";
import CareersJob from "./pages/CareersJob";
import CandidateHome from "./pages/CandidateHome";
import Approvals from "./pages/Approvals";
import Sourcing from "./pages/Sourcing";
import HROneSync from "./pages/HROneSync";
import RankedShortlist from "./pages/RankedShortlist";
import ExternalDecision from "./pages/ExternalDecision";
import "./App.css";

function Protected({ children, allow }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground tracking-widest uppercase">Loading TalentOS AI…</div>
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout><ErrorBoundary>{children}</ErrorBoundary></Layout>;
}

const ADMIN_ROLES = ["super_admin", "group_hr_head", "entity_hr_head", "integration_admin"];
const HRONE_ROLES = [...ADMIN_ROLES, "recruiter"];
const AUDITOR_ROLES = [...ADMIN_ROLES, "auditor"];

function RoleHome() {
  const { user } = useAuth();
  if (user?.role === "candidate") return <CandidateHome />;
  if (user?.role === "interview_panelist") return <Interviews />;
  if (user?.role === "finance_approver") return <Approvals />;
  return <Dashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/careers/:id" element={<CareersJob />} />
          <Route path="/decision/:token" element={<ExternalDecision />} />
          <Route path="/" element={<Protected><RoleHome /></Protected>} />
          <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
          <Route path="/sourcing" element={<Protected><Sourcing /></Protected>} />
          <Route path="/requisitions" element={<Protected><Requisitions /></Protected>} />
          <Route path="/requisitions/:id" element={<Protected><RequisitionDetail /></Protected>} />
          <Route path="/candidates" element={<Protected><Candidates /></Protected>} />
          <Route path="/shortlist" element={<Protected><RankedShortlist /></Protected>} />
          <Route path="/candidates/:id" element={<Protected><CandidateDetail /></Protected>} />
          <Route path="/agent" element={<Protected><AgentChat /></Protected>} />
          <Route path="/interviews" element={<Protected><Interviews /></Protected>} />
          <Route path="/offers" element={<Protected><Offers /></Protected>} />
          <Route path="/master-data" element={<Protected allow={ADMIN_ROLES}><MasterData /></Protected>} />
          <Route path="/integrations" element={<Protected allow={ADMIN_ROLES}><Integrations /></Protected>} />
          <Route path="/hrone" element={<Protected allow={HRONE_ROLES}><HROneSync /></Protected>} />
          <Route path="/audit" element={<Protected allow={AUDITOR_ROLES}><AuditLog /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
